// The prompts the agent-queue buttons write, as templates. Each button computes its variables from live data
// (src/lib/agentPrompts.ts lists them per prompt) and renders the newest saved template from public.agent_prompts,
// falling back to the built-in default below. Agents → Prompts edits them; a saved prompt wins over this file.
//
// Plain JS so the monthly keyword sweep (scripts/lib/keyword-sweep.mjs, netlify/functions/keyword-sweep-scheduled.mjs)
// renders from the same defaults as the app. Types in promptTemplates.d.mts.

/** {{name}}: lowercase letters, digits and underscores. */
export const PROMPT_VAR = /\{\{\s*([a-z0-9_]+)\s*\}\}/g;

/** The variable names a template uses, in order of first use. */
export function templateVars(template) {
  return [...new Set([...String(template).matchAll(PROMPT_VAR)].map((m) => m[1]))];
}

/**
 * Fill a template. A line whose variables all came out empty is dropped (so "Secondary: {{secondary}}" disappears
 * when there are none), runs of blank lines collapse to one, and the result is trimmed.
 */
export function renderPrompt(template, vars) {
  const value = (k) => (vars[k] == null ? '' : String(vars[k]));
  const out = [];
  for (const line of String(template).split('\n')) {
    const names = [...line.matchAll(PROMPT_VAR)].map((m) => m[1]);
    if (names.length && names.every((k) => !value(k).trim())) continue;
    out.push(line.replace(PROMPT_VAR, (_, k) => value(k)));
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

export const PROMPT_DEFAULTS = {
  'retro-opt': `Retroactive Optimization — agent queue.
Client: {{client}}
Page: {{page_url}}
Cockpit page id: {{page_id}}
Primary phrase: {{primary_phrase}}

Why: {{why}}

{{search_console}}

Follow the retro-opt skill end to end. Spam-penalty risk first, then the full checklist, every number from a script.
Spec = two sections, exactly "## Analysis" then "## Plan". Build report = the Execution doc. Approval = Verify.
Never ask the client for materials. Build from the live page, live data and this page’s earlier journal entries,
not from session transcripts or files outside this job.`,

  'density-fix': `Keyword Density Fix — agent queue.
Client: {{client}}
Page: {{page_url}}
Cockpit page id: {{page_id}}

Bring each of these into 0.5–1% of the body words (Kyle's bar): cut the ones over 1%, work in the ones under 0.5%.
{{phrases}}

Why: {{why}}

{{search_console}}
The counts above are Cockpit's screen (body without nav/footer). Re-measure with the skill's script before quoting a number.

Follow the keyword-density-fix skill end to end. Spec = two sections, exactly "## Analysis" then "## Plan".
Build report = the Execution doc. Approval = Verify.
Re-measure every phrase first and decide over, under or in line yourself; the screen can be wrong. Cut repetitions rather than
swapping in forced synonyms; add a phrase only where a reader would expect it (heading, opening, a real section), never as filler.
Never ask the client for materials. Build from the live page, live data and this page’s earlier journal entries.`,

  'links-fix': `Internal Links Fix — agent queue.
Client: {{client}}
Page: {{page_url}}
Cockpit page id: {{page_id}}

Internal links pointing at this page: {{inlinks}} (Ahrefs crawl). The floor is 5: get it there with links from pages whose
readers would genuinely want this page next, never from pages that only share a word with it.

Why: {{why}}

{{search_console}}

Follow the internal-links-fix skill end to end. Spec = two sections, exactly "## Analysis" then "## Plan".
Build report = the Execution doc. Approval = Verify.
Measure first with scripts/internal-links.mjs (eczzz/cockpit); every count in the spec comes from it. One new link per source
page, in its own copy, never in nav, footer or a sitewide block, and never on a page already at its link cap. Lock every
file you edit. Never ask the client for materials.`,

  'template-fix': `Templated Content Fix — agent queue.
Client: {{client}}
Page: {{page_url}}
Cockpit page id: {{page_id}}

{{share}} of this page's text (8-word runs, place names masked) is also on its closest sibling. Siblings it shares with:
{{siblings}}

Why: {{why}}

{{search_console}}

Follow the templated-content-fix skill end to end. Spec = two sections, exactly "## Analysis" then "## Plan".
Build report = the Execution doc. Approval = Verify.
Build this page new, from scratch, for the person searching for it: who they are, what they want, what holds them back, and the one action the page asks of them (the buyer brief). Every section moves them toward that action. Unique content follows from that; the overlap check (under 20% against every sibling) only confirms it. Keep keyword density 0.5–1%.
Never ask the client for materials. Never invent people, jobs, prices or reviews.`,

  'offer-ideation': `Offer Ideation — agent queue.
Client: {{client}}
Cockpit client id: {{client_id}}
Starting point: {{competencies}} competencies on file, {{offers}} offers already (ideated, production or denied).

Steer: {{steer}}

Follow the offer-ideation skill end to end. Read context with \`node --env-file=.env scripts/offers.mjs context <client>\`.
Fill gaps in the competency inventory first (add, with proof), then propose offers that a real demand signal backs.
Write proposals with \`scripts/offers.mjs propose <job-id> --from-json -\`; that closes this job. Never re-propose a denied offer.
Never ask the client for materials. Never invent proof.`,

  'google-ads-checkin': `Google Ads account check-in — agent queue.
Client: {{client}}
Cockpit client id: {{client_id}}
Google Ads account: {{customer_id}} (under the Bullfinch MCC)
Leads counted as: {{leads}}
Queued by: {{trigger}}

Follow the google-ads-checkin skill end to end, every checklist step, in order.
Work from an eczzz/cockpit checkout with .env (SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY): read the account with
\`node --env-file=.env scripts/google-ads.mjs gaql {{customer_id}} "<query>"\`, and the journal with \`cockpit ads\`.

Spec = the check-in. Two sections, exactly "## Check-in" (scoreboard, what our last moves did, leakage, changes by others,
where money leaks, lead quality, the one biggest lever) then "## Proposals". Every proposal is also a decision
(options "Do it" / "Skip", with your recommendation) so the human approves them one by one.
No account changes at spec. Write the check-in journal entry and update the pinned state before submitting the spec.

Build = apply only the proposals answered "Do it" (unanswered = your recommendation): validate-only first, then live,
then read back; a rollback file for every change; a journal "change" entry per change with a review-by date.
Never re-pitch a won't-do from the journal. Never invent numbers: every figure comes from a query you ran.`,

  'keyword-sweep': `KEYWORD SWEEP — {{client}}. Not a spec: do not write a spec or an operator summary.
Run: {{run_id}}

Work from an eczzz/cockpit checkout that has scripts/keyword-sweep.mjs, with .env holding
SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY and AHREFS_MCP_KEY.

1. node --env-file=.env scripts/keyword-sweep.mjs "{{client}}" settings
   If services or service_areas are empty, fill them from the client KB (cockpit kb list/get,
   canon + Services) and their live service pages: settings --json '{"services":[{"name","url","terms":[]}],"service_areas":[]}'.
2. node --env-file=.env scripts/keyword-sweep.mjs "{{client}}" discover --run {{run_id}}
   Pulls Ahrefs inside the unit cap and drops what they already rank for, what is out of
   reach for their DR, duplicates, and anything a person has denied. Stops cleanly at the cap.
3. node --env-file=.env scripts/keyword-sweep.mjs "{{client}}" candidates --run {{run_id}} --json
   Read them next to the KB and client_pages. Write proposals.json: an array of
   {keyword, cluster[], topic, tier, tier_reason, content_type, title, intended_url, is_new_page, pitch}.
   A = commercial/transactional/local intent for a service they sell → landing_page or
   landing_page_optimise (existing URL). B = relevant, unlikely to convert → article, or tool when
   calculators own the SERP. About 6 A and 12 B. Pitch is two lines a person can say yes or no to.
   No invented claims: the pitch says what the page is for, not what the client has done.
4. node --env-file=.env scripts/keyword-sweep.mjs "{{client}}" propose --run {{run_id}} --from-json proposals.json
   Enforces quotas and deny rules, writes the queue rows A above B, posts the chat summary and
   closes this job. If it refuses a row, fix the row or drop it; never work around a rule.`,

  'send-to-build': `Write a SPEC (not a build) for {{what}} for {{client}}.
Target: {{target}}
Intended URL: {{intended_url}}.
{{context}}

The spec must include:
1. Search intent in one paragraph: who is searching and what they need to do next.
2. Section outline built from what ranks: pull the top 10 (serp-overview), list the sections and questions they share, and what they miss.
3. Proof points from the client’s KB (quote the file) — no invented claims, numbers or credentials.
4. Internal links: which existing pages link in (from client_pages) and where this page links out.
5. The CTA and where it sits.
6. Schema (type and the fields you will fill).
7. Meta title (≤ 60 chars) and meta description (≤ 155 chars).
{{type_item}}

SEO gate at build (write it into the spec as a required build step): once the page renders on the deploy preview,
extract its main content to markdown (headings, paragraphs, lists, links with their hrefs, image alt text; no nav or
footer) and run the content-seo-review script on it: --primary "<target>", a --secondary for each cluster term,
--domain <client domain>, landing-page thresholds --min-words 500 --max-words 3000 --min-internal-links 2, and
the Voice file’s banned terms if the client has one. Any FAIL means revise and re-run before the PR. Never report a
count the script did not produce. The readout goes in the build report as an "SEO review" section, with the
judgment items that apply to a landing page (real evidence, link targets resolve, question headings answered).

{{repo}}
Content queue row: {{row_id}}. Normal spec approval applies before any build.`,

  'send-to-build-page': `Page Build: a new landing page for {{client}}, built to win "{{keyword}}".
Target: {{target}}
Intended URL: {{intended_url}}.
{{context}}

This is a Page Build job: follow the page-build skill end to end.
- Spec, keyword: "{{keyword}}", source "given" (the Content Queue chose it). Secondaries from the cluster: {{secondary}}
- Spec, buyer brief: who searches this, what they want in their words, what they already know, what holds them back, the page's one promise and the one action it asks for.
- Spec, sections: direction, not copy, in an order that persuades that buyer into the action. What each section is and the idea it has to get across, informed by what the top 10 results cover and miss (serp-overview). Source material only where the client has supplied some; a generated image on a few sections; one or two signature moments, the rest restrained.
- Proof only from the client's KB (quote the file) and their live site. No invented claims, numbers, reviews or credentials.
- Also in the spec: meta title (≤ 60 chars) and meta description (≤ 155 chars), the CTA and where it sits, schema type and the fields you will fill from real facts, and internal links (which existing pages link in, from client_pages, and where this page links out).
- Build: each section by its own agent from cockpit ai section-prompt, then the SEO review on the preview, Lighthouse included.

{{repo}}
Content queue row: {{row_id}}. Normal spec approval applies before any build.`,

  'send-to-writer': `Content brief
Primary: {{primary}}
Secondary: {{secondary}}
Repo: {{repo}}
{{attachments}}`,

  'seo-diagnosis': `SEO diagnosis — agent queue.
Client: {{client}}

Read the live page and the repo behind each finding, then write the spec: one action per
finding, the exact copy where the action is a rewrite, and "no action" wherever nothing
on-site would move it. Never recommend adding the phrase in more places.

Follow the seo-diagnosis skill: run its full checklist on every finding, measured on the
rendered live page against the pages that rank for the query, not against our own pages.

Findings:
{{findings}}

Opportunity ids: {{opportunity_ids}}`,

  'hosting-fix-wordpress': `Managed hosting fixes — agent queue.
Client: {{client}} (hosting account: {{account}})
Site: {{site_url}}
Platform: WordPress on Cloudways · server {{server_id}} · app {{app_id}}
Database: the site’s own WordPress database, through WP-CLI only. Nothing in Cockpit.
From the hosting checklist run of {{run_when}} (Cockpit → Hosting → {{account}}; run id {{run_id}}). The step details there have the updates, Wordfence findings, SMTP test and screenshots.

Fix these:
{{fixes}}

{{not_yours}}

How to reach the site (run from the live runner, cd /Users/owlbert/.openclaw/hosting-runner/scripts/hosting-runner; <env> = /Users/owlbert/.openclaw/workspace-talon/credentials/hosting-runner.env):
- Snapshot (database + plugins/themes, kept on the server): node --env-file=<env> runner.mjs try {{app_id}} --steps snapshot
- WP-CLI: node --env-file=<env> runner.mjs wp {{app_id}} -- <args>   (e.g. -- plugin list --update=available)
- Re-check: node --env-file=<env> runner.mjs try {{app_id}} --steps updates,smtp,security

Rules:
- Follow the managed-hosting skill, section "WordPress sites (Cloudways)".
- There is no deploy preview: the live site is the only copy. The spec only reads and proposes. The build takes a snapshot first, then makes the approved changes.
- Maintenance, not a redesign: screenshot the key pages before and after, desktop and mobile, and compare them.
- Never delete a plugin, theme or user, and never edit content, without an approved decision in the spec. Deactivating something counts as a change.
- Premium plugins that need a licence, a login only the client or Kyle has, or a paid upgrade: stop and name exactly what is needed.
- Say in the report which findings your changes should clear; the hosting checklist is re-run from Cockpit to confirm.`,

  'hosting-fix-netlify': `Managed hosting fixes — agent queue.
Client: {{client}} (hosting account: {{account}})
Site: {{site_url}}
Repo: {{repo}} (branch {{branch}}){{netlify_site}}
Database: none (dependency, config and header changes only; no SQL).
From the hosting checklist run of {{run_when}} (Cockpit → Hosting → {{account}}; run id {{run_id}}). The step details there have the full audit, versions and headers.

Fix these:
{{fixes}}

{{not_yours}}

Rules:
- Follow the managed-hosting skill: it says what matters on a Netlify site, what to leave alone, and how to review a Dependabot PR.
- Maintenance, not a redesign: the site must look and behave the same. Compare the deploy preview with production page by page.
- Your own fixes: one branch, one PR against {{branch}}. Dependabot PRs: review on their own branch and push fixes there. Never merge, never deploy, never touch production env vars.
- Never apply or recommend a downgrade, and never chase an advisory that has no fix released.
- If you change the build, pin Node with .nvmrc (22 unless the build needs otherwise) so the preview builds the way production will.
- Major upgrades (e.g. Astro 5 → 7) follow the official upgrade guides. If one is too risky for this PR, do the safe part and say exactly what is left in the report.
- After the human merges, the hosting checklist is re-run from Cockpit to confirm; say in the report which findings your PR should clear.`,

  'page-build-section': `PAGE BUILD · SECTION {{section_number}} OF {{section_count}}
Client: {{client}}
Page: {{page_url}}
Job: {{job_id}}

You are building ONE section of this page, and only this one. The rest of the page is context so your section belongs to it; it is not your work. Do not touch any other section.

WHO THIS PAGE IS FOR
{{buyer}}

THE OFFER THIS PAGE SELLS
{{offer}}

YOUR SECTION
Working heading: {{heading}} (H{{level}})
What it is: {{purpose}}
What it has to get across: {{message}}
{{source}}
{{image}}
{{keyword}}

THE WHOLE PAGE, IN ORDER
{{outline}}

THE JOB OF THIS SECTION
Move the person above one step closer to the action. Write to them, in their words: what they want, what worries them, what they would ask. If a stranger skimming for five seconds would not get what this section says and why it matters to them, it has failed, however good it looks. No insider language: the agency's process, philosophy and people's names appear only where the buyer needs them to decide. Never invent people, jobs, prices, reviews or credentials.

THE DESIGN BAR
Restrained and tasteful: clear type, generous space, one idea per section, built from the site's own tokens, type scale and components, so the page reads as one calm, confident whole. Clarity beats cleverness every time. A novel format earns its place only when it makes the point land faster.
Design this page from its own place and buyer. Use the site's tokens and components, never another page's layout: don't open sibling city pages or other Page Builds for design reference, so no two pages share a hero.
{{signature}}

It must be flawless at 390, 768 and 1440 px wide. Keep text as real text (none baked into images), headings in order, contrast readable, and honour prefers-reduced-motion.

WHEN IT'S BUILT
Run the site locally, screenshot the section at 390, 768 and 1440, look at every screenshot and fix what you see, then report back: the files you changed, the screenshot paths, and one line on how the section moves the buyer toward the action.`,

  'hosting-setup': `Hosting setup — agent queue.
Hosting account: {{account}}
Site: {{site_name}} ({{site_url}})
Cockpit monitored_sites id: {{site_id}} · Platform: {{platform}}
Database: Cockpit (rsobqxbibblkmurrnjdh), monitored_sites row above only.

Follow the managed-hosting skill, section "Set up a site". Decide and propose in the spec:
1. site_kind: marketing (no logins, payments or customer data on our side) or app.
2. The URL the checklist should load (the live homepage, never a sitemap or a redirect).
3. hosting_skip_steps: steps that cannot apply to this site, each with a one-line reason.
4. Netlify sites with a repo: Dependabot alerts on, security update PRs on, .github/dependabot.yml from the skill.
5. Anything this site needs watched by hand (a form, a booking embed, a store).

Build applies the approved values: the monitored_sites update (read it back), the GitHub settings, and the dependabot.yml PR. Never change the live site itself.`,

  'client-feedback': `Client feedback — {{client}}. {{count}} note(s) {{sender}} pinned in {{source}} with feedback mode.
Each note below was dropped on the exact spot it is about. The pin is where the finger landed, not always the thing to change: read the element, the nearby controls, the heading and the screenshot together before deciding what was meant.
Notes with "record" name the record on screen: read that row (and its activity trail, included) in the client's database before deciding a bug is a bug.
Questions: answer them in the spec in plain words for the client. Changes and bugs: spec them as one release. A note that asks for something big (an integration, a new screen) is still a request; spec it like any other.
Every note keeps its own id: the client's app shows each one's status, so say per note what was done.
Batch note from the client: {{batch_note}}
Full notes (JSON with screenshots, record context, errors): Comms → Feedback, batch {{batch_id}}

{{notes}}`,

  'security-audit': `Security Audit — {{system}}. Follow the workspace skill security-audit end to end.
Cockpit system: {{system_id}} ({{kind}})
Client: {{client}}
Database: {{database}}
Repos:
{{repos}}
Sites:
{{sites}}

Saved cascade calls: {{cascade_calls}}. They are in the brief (cockpit ai get, or cockpit security calls {{system_id}}); never ask about a saved link again.
Spec = the report: findings with proof, each with its Fix / Skip / Later decision. Build fixes only what was marked Fix.`,
};

/**
 * The variables of the Page Build section prompt, from one section and the page around it. cockpit-cli
 * (`cockpit ai section-prompt`) renders the same prompt for the build agent with the same rules; keep them in step.
 */
/**
 * The page's offer as a section prompt block: its name, the ideas pages for it get across (each in this page's own
 * words) and the ones they never say. `offer` = { name, include_ideas, exclude_ideas } from client_offers.
 */
export function offerBlock(offer) {
  const lines = (t) => String(t || '').split('\n').map((l) => l.trim()).filter(Boolean);
  if (!offer?.name) return '(No offer linked to this page. Sell the service the page is about, from the buyer brief.)';
  const include = lines(offer.include_ideas);
  const exclude = lines(offer.exclude_ideas);
  return [
    `Offer: ${offer.name}`,
    include.length
      ? `Ideas every page for this offer gets across somewhere (the spec placed them; carry the ones your section's message calls for, in fresh words for this buyer and search, never a stock sentence):\n${include.map((l) => `- ${l}`).join('\n')}`
      : null,
    exclude.length ? `Never say or imply, in any form:\n${exclude.map((l) => `- ${l}`).join('\n')}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

/** The spec's buyer brief as the block every section prompt opens with. */
export function buyerBlock(buyer) {
  if (!buyer || !buyer.who) return '(No buyer brief in the spec. Write for the person searching the target keyword who wants the service this page sells.)';
  return [
    `Who: ${buyer.who}`,
    buyer.wants ? `What they want: ${buyer.wants}` : null,
    buyer.knows ? `What they already know or believe: ${buyer.knows}` : null,
    buyer.objections?.length ? `What holds them back: ${buyer.objections.join('; ')}` : null,
    buyer.promise ? `The page's promise: ${buyer.promise}` : null,
    buyer.action ? `The one action we want: ${buyer.action}` : null,
  ]
    .filter(Boolean)
    .join('\n');
}

export function pageSectionVars({ client, pageUrl, jobId, sections, index, keyword = null, buyer = null, offer = null }) {
  const s = sections[index];
  const kw = keyword?.primary?.trim();
  const also = (keyword?.secondary ?? []).filter(Boolean);
  const outline = sections
    .map((x, i) => `${String(i + 1).padStart(2, '0')}. ${x.heading}: ${x.purpose || ''}${x.image?.idea ? ' [image]' : ''}${i === index ? '   ← YOU' : ''}`)
    .join('\n');
  return {
    client,
    page_url: pageUrl,
    job_id: jobId,
    section_number: String(index + 1),
    section_count: String(sections.length),
    heading: s.heading,
    level: String(s.level ?? 2),
    purpose: s.purpose || '',
    message: s.message || '',
    source: s.source?.trim() ? `Source material, include it as given:\n${s.source.trim()}` : '',
    image: s.image?.idea?.trim()
      ? `Generated image wanted: ${s.image.idea.trim()}\nMake it with image generation and use it with intent: one strong image beats several decorative ones. Look at every result before you use it; no text, logos or brand marks in the image; save it to the repo as an optimised webp.`
      : 'No generated image in this section: carry it with type, layout, colour and motion.',
    outline,
    buyer: buyerBlock(buyer),
    offer: offerBlock(offer),
    // The hero is always a signature moment (Kyle, 2026-10-10: Pueblo and Durango heroes came out flat under the restraint rule).
    signature: index === 0 || s.level === 1
      ? 'THIS IS THE HERO: the first five seconds, and always a signature moment whatever the spec says. Make it the most striking first screen this site has: a bold composition built around this place and this buyer\'s outcome (a full-bleed photo of the place, an interaction, motion that lands the promise), never a headline beside a boxed photo with a caption. Still instantly clear: what this is, who it is for, and the one button.'
      : s.signature
      ? 'THIS IS A SIGNATURE MOMENT. The page stays quiet so this can hit: one bold, memorable piece of motion or interaction that makes this section\'s point unforgettable. Make it the best thing on the page.'
      : 'This is not a signature moment: keep motion to subtle, purposeful transitions. The page saves its punch for its one or two signature sections.',
    keyword: kw
      ? `Target search for the page: "${kw}"${also.length ? ` (also: ${also.join(', ')})` : ''}. Use it only where a reader would expect it, never as filler: the page as a whole holds it at 0.5–1% of the words${s.level === 1 ? ', and this H1 section carries it in the heading and the opening lines' : ''}. The SEO review counts it after the build.`
      : '',
  };
}

/** Newest saved template for a prompt, via any `sb(method, path)` PostgREST helper (scripts). Null = use the default. */
export async function savedTemplateVia(sb, promptId) {
  const [row] = await sb('GET', `agent_prompts?prompt_id=eq.${encodeURIComponent(promptId)}&select=template&order=created_at.desc&limit=1`);
  return row?.template ?? null;
}
